import React, { useState, useEffect } from 'react';
import { useCRM } from '../context/CRMContext';
import { useBrreg } from '../context/BrregContext';
import { BrregService } from '../services/BrregService';
import SafeIcon from '../common/SafeIcon';
import PermissionGate from '../common/PermissionGate';

const BrregSettings = () => {
  const { projects, pipelineStages } = useCRM();
  const { settings: contextSettings } = useBrreg();
  
  const [saving, setSaving] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('unknown'); // unknown, checking, ok, error
  const [latency, setLatency] = useState(null);

  const [formData, setFormData] = useState({
    brreg_enabled: true,
    api_source: 'OpenAPI', // OpenAPI, Proxy, Bisnode
    api_key: '', // For premium providers
    allowed_roles: ['Admin', 'SalesManager'],
    allow_import: true,
    enable_roles_enrichment: false, // Premium feature mock
    default_project_id: '',
    default_pipeline_stage_id: '',
    default_gdpr_status: 'Berettiget interesse'
  });

  useEffect(() => {
    if (contextSettings) {
      setFormData(prev => ({ ...prev, ...contextSettings }));
    }
    checkApiConnection();
  }, [contextSettings]);

  const checkApiConnection = async () => {
    setConnectionStatus('checking');
    const result = await BrregService.checkConnection();
    if (result.status === 'OK') {
      setConnectionStatus('ok');
      setLatency(result.latency);
    } else {
      setConnectionStatus('error');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving to DB
      await new Promise(r => setTimeout(r, 800));
      alert('Innstillinger lagret!');
    } catch (e) {
      console.error(e);
      alert('Feil ved lagring');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    setFormData(prev => {
      const roles = prev.allowed_roles || [];
      if (roles.includes(role)) {
        return { ...prev, allowed_roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, allowed_roles: [...roles, role] };
      }
    });
  };

  return (
    <PermissionGate permission="canImportBrreg">
      <div className="p-8 max-w-4xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex justify-between items-center border-b border-slate-200 pb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <SafeIcon icon="🏢" className="w-5 h-5" />
              </div>
              <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Datakilder</span>
            </div>
            <h1 className="text-3xl font-black text-slate-900">Enhetsregisteret (BRREG)</h1>
            <p className="text-slate-500 font-medium mt-1">Konfigurasjon av datakilde og API-tilkobling.</p>
          </div>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <SafeIcon icon="🔄" className="animate-spin w-4 h-4" /> : <SafeIcon icon="💾" className="w-4 h-4" />}
            Lagre endringer
          </button>
        </div>

        {/* API Status Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Systemstatus</h3>
              <p className="text-sm text-slate-500">Tilkobling mot data.brreg.no</p>
            </div>
            <div className="flex items-center gap-4">
                {connectionStatus === 'checking' && (
                    <span className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-lg text-xs font-bold flex items-center gap-2">
                        <SafeIcon icon="🔄" className="animate-spin" /> Tester...
                    </span>
                )}
                {connectionStatus === 'ok' && (
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold flex items-center gap-2 border border-emerald-100">
                        <SafeIcon icon="✅" /> Tilkoblet ({latency}ms)
                    </span>
                )}
                {connectionStatus === 'error' && (
                    <span className="px-3 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-bold flex items-center gap-2 border border-red-100">
                        <SafeIcon icon="❌" /> Ingen kontakt
                    </span>
                )}
                <button onClick={checkApiConnection} className="text-xs font-bold text-blue-600 hover:underline">Test på nytt</button>
            </div>
          </div>
        </div>

        {/* API Configuration */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <SafeIcon icon="🔌" className="text-slate-400" /> API Oppsett
          </h3>
          
          <div className="space-y-6">
            <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Dataleverandør</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.api_source === 'OpenAPI' ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-bold text-slate-900">Åpent API</span>
                            <input type="radio" name="api_source" value="OpenAPI" checked={formData.api_source === 'OpenAPI'} onChange={e => setFormData({...formData, api_source: e.target.value})} className="hidden" />
                            {formData.api_source === 'OpenAPI' && <SafeIcon icon="✅" className="text-blue-600" />}
                        </div>
                        <p className="text-xs text-slate-500">Gratis. Grunndata kun. Ingen roller eller regnskap.</p>
                    </label>

                    <label className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.api_source === 'Bisnode' ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-bold text-slate-900">Premium (Bisnode/D&B)</span>
                            <input type="radio" name="api_source" value="Bisnode" checked={formData.api_source === 'Bisnode'} onChange={e => setFormData({...formData, api_source: e.target.value})} className="hidden" />
                            {formData.api_source === 'Bisnode' && <SafeIcon icon="✅" className="text-blue-600" />}
                        </div>
                        <p className="text-xs text-slate-500">Krever abonnement. Inkluderer roller, regnskap og rating.</p>
                    </label>
                </div>
            </div>

            {formData.api_source !== 'OpenAPI' && (
                <div className="animate-in fade-in slide-in-from-top-2">
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">API Nøkkel / Token</label>
                    <input 
                        type="password" 
                        value={formData.api_key} 
                        onChange={e => setFormData({...formData, api_key: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="Lim inn nøkkel fra leverandør..."
                    />
                </div>
            )}
          </div>
        </div>

        {/* Data Enrichment Options */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <SafeIcon icon="📦" className="text-slate-400" /> Databerikelse
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center p-4 border border-emerald-100 bg-emerald-50/30 rounded-xl">
              <SafeIcon icon="✅" className="text-emerald-500 w-5 h-5 mr-3" />
              <div className="flex-1">
                <p className="font-bold text-slate-900 text-sm">Enhetsregisteret (Grunndata)</p>
                <p className="text-xs text-slate-500">Navn, org.nr, adresse, bransje, status.</p>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-white px-2 py-1 rounded border border-emerald-100">Alltid Inkludert</span>
            </div>

            <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.enable_roles_enrichment ? 'border-primary-200 bg-primary-50/30' : 'border-slate-200 bg-white'}`}>
              <input 
                type="checkbox" 
                checked={formData.enable_roles_enrichment} 
                onChange={e => setFormData({...formData, enable_roles_enrichment: e.target.checked})}
                className="w-5 h-5 rounded text-primary-600 mr-3" 
                disabled={formData.api_source === 'OpenAPI'}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-900 text-sm">Roller & Ledelse</p>
                    {formData.api_source === 'OpenAPI' && <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 rounded uppercase font-bold">Krever Premium</span>}
                </div>
                <p className="text-xs text-slate-500">Daglig leder, styreleder og styremedlemmer.</p>
              </div>
            </label>
          </div>
        </div>

        {/* Default Rules (Same as before) */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <SafeIcon icon="⚖️" className="text-slate-400" /> Standardregler ved import
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Default Prosjekt</label>
              <select 
                value={formData.default_project_id} 
                onChange={e => setFormData({...formData, default_project_id: e.target.value})}
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">-- Ingen standard --</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Default Pipeline Steg</label>
              <select 
                value={formData.default_pipeline_stage_id} 
                onChange={e => setFormData({...formData, default_pipeline_stage_id: e.target.value})}
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">-- Ingen standard --</option>
                {pipelineStages.map(s => <option key={s.stage_id} value={s.stage_id}>{s.name}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Standard GDPR Status</label>
              <div className="flex gap-4">
                {['Berettiget interesse', 'Samtykke', 'Usikker'].map(status => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="gdpr" 
                      checked={formData.default_gdpr_status === status}
                      onChange={() => setFormData({...formData, default_gdpr_status: status})}
                      className="text-primary-600 focus:ring-primary-500" 
                    />
                    <span className="text-sm font-medium text-slate-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </PermissionGate>
  );
};

export default BrregSettings;